/**
 * Os três diálogos de "Alterar" da seção Configuração.
 *
 * ── Um por BLOCO, e não um por tela ───────────────────────────────────────
 *
 * A alteração é do grupo. Um "Alterar" no topo da seção abriria um formulário
 * de seis campos para trocar o fuso — e quem veio trocar o fuso teria de
 * passar os olhos pela regra que abre negócio, que é a mais perigosa do
 * produto, para chegar no campo que queria.
 *
 * ── A armadilha: PATCH de regra REESCREVE a regra inteira ─────────────────
 *
 * `criar_oportunidade_regra` e `encerramento_regra` são colunas `jsonb`, e o
 * `UPDATE` faz `COALESCE($3::jsonb, ...)` — o objeto que chega SUBSTITUI o que
 * estava lá, campo a campo não.
 *
 * Mandar só `{ canais }` apagaria `etapas_abertas`, `janela_dias` e `risco` na
 * mesma escrita. E o estrago não apareceria aqui: `etapas_abertas` decide o que
 * é etapa final no funil, `janela_dias` decide se a C17 abre um negócio novo ou
 * reaproveita o aberto, e `risco` pinta o cartão de âmbar. Três telas passariam
 * a se comportar diferente por causa de uma troca de canal.
 *
 * Por isso todo envio espalha a regra ATUAL antes de sobrescrever o que mudou.
 *
 * ── A justificativa é escrita aqui, e não no step-up ──────────────────────
 *
 * A rota exige `justificativa` no corpo, com dez caracteres no mínimo. O
 * step-up também pede uma, e são coisas diferentes: a dele autoriza a sessão, a
 * daqui explica a mudança e fica em `audit_logs` com o antes e o depois. É essa
 * que a própria tela mostra no rodapé do bloco.
 */

import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ConfiguracaoDoTenant } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { useSessao } from '@/shared/sessao/contexto';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoArea, CampoSelecao, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import type { UsoDeStepUp } from '@/features/acesso/confirmar-identidade';

/** O mínimo que a rota aceita. Dito na tela, e não descoberto na recusa. */
const MINIMO_DA_JUSTIFICATIVA = 10;

const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;

/**
 * Os fusos que uma revenda brasileira usa.
 *
 * Uma lista curta, e não `Intl.supportedValuesOf('timeZone')` com seiscentos
 * itens: quem administra uma revenda escolhe entre estes, e uma caixa com
 * "Africa/Abidjan" no topo é onde se erra por rolagem. O servidor valida o
 * fuso de qualquer jeito — a lista aqui é conveniência, não regra.
 */
const FUSOS = [
  'America/Sao_Paulo',
  'America/Bahia',
  'America/Fortaleza',
  'America/Recife',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Noronha',
] as const;

/**
 * Os canais que a regra pode disparar.
 *
 * O valor é o do Chatwoot, e o nome é o que a pessoa conhece. Mostrar
 * `Channel::FacebookPage` numa caixa de seleção é pedir que ela traduza o
 * vocabulário do motor.
 */
const CANAIS = [
  { valor: 'Channel::Whatsapp', nome: 'WhatsApp' },
  { valor: 'Channel::Instagram', nome: 'Instagram' },
  { valor: 'Channel::FacebookPage', nome: 'Facebook' },
  { valor: 'Channel::Telegram', nome: 'Telegram' },
  { valor: 'Channel::WebWidget', nome: 'Site' },
  { valor: 'Channel::Email', nome: 'E-mail' },
  { valor: 'Channel::Api', nome: 'Integração (portais e API)' },
] as const;

type Corpo = Parameters<NonNullable<ReturnType<typeof useApi>>['alterarConfiguracao']>[0]['corpo'];

/**
 * A casca comum dos três: justificativa, salvar, e o step-up por cima.
 *
 * Sem ela, cada diálogo repetiria o campo de justificativa, a regra dos dez
 * caracteres, o `comStepUp` e a tradução do erro — quatro cópias que envelhecem
 * separadas, e a primeira a envelhecer é a mensagem de recusa.
 */
function DialogoDeAlteracao({
  titulo,
  stepUp,
  aoFechar,
  aoSalvar,
  montarCorpo,
  children,
}: {
  titulo: string;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoSalvar: () => void;
  montarCorpo: (justificativa: string) => Corpo;
  children: ReactNode;
}): ReactNode {
  const api = useApi();
  const { recarregarMe } = useSessao();
  const [justificativa, definirJustificativa] = useState('');
  const [recusa, definirRecusa] = useState<string | null>(null);
  const curta = justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA;

  const salvar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      definirRecusa(null);
      /*
       * O corpo é montado uma vez e reusado na repetição do step-up: se ele
       * fosse remontado, a segunda tentativa leria o estado de novo — e um
       * campo alterado entre o 403 e a concessão iria junto sem ninguém ver.
       */
      const corpo = montarCorpo(justificativa.trim());
      await stepUp.comStepUp(async () => {
        await api.alterarConfiguracao({ corpo });
        // As preferências vão no `/v1/me`: a tela as relê sem esperar o aviso.
        void recarregarMe();
      }, 'configuracao');
    },
    onSuccess: aoSalvar,
    onError: (erro: unknown) =>
      definirRecusa(recadoDeErro(erro, { generica: 'Não foi possível salvar a alteração.' })),
  });

  return (
    <Dialogo
      titulo={titulo}
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={curta || salvar.isPending}
            motivo={
              salvar.isPending
                ? 'Salvando…'
                : `Escreva o motivo — ao menos ${MINIMO_DA_JUSTIFICATIVA} caracteres.`
            }
            onClick={() => salvar.mutate()}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <div className="alterar-config">
        {children}

        {/*
          A justificativa fica por ÚLTIMO, depois do que mudou.
          Escrita antes, ela é sobre uma mudança que a pessoa ainda não fez — e
          o que sai é "ajuste", que é o mesmo que campo vazio com mais passos.
        */}
        <CampoArea
          rotulo="Por que está mudando"
          value={justificativa}
          rows={3}
          maxLength={500}
          ajuda="Fica no registro de auditoria, com o antes e o depois, e aparece no rodapé deste bloco."
          onChange={(e) => definirJustificativa(e.currentTarget.value)}
        />

        {salvar.isError && recusa && <Faixa tom="bloqueado">{recusa}</Faixa>}
      </div>
    </Dialogo>
  );
}

/* ── Bloco 1: a revenda ─────────────────────────────────────────────────── */

export function AlterarARevenda({
  config,
  stepUp,
  aoFechar,
  aoSalvar,
}: {
  config: ConfiguracaoDoTenant;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoSalvar: () => void;
}): ReactNode {
  const [fuso, definirFuso] = useState(config.timezone);
  const [janelas, definirJanelas] = useState(() =>
    DIAS.map((_, dia) => {
      const existente = config.horario_de_atendimento.find((j) => j.diaDaSemana === dia);
      return {
        aberto: existente !== undefined,
        abre: existente?.abre ?? '08:00',
        fecha: existente?.fecha ?? '18:00',
      };
    }),
  );

  const trocar = (dia: number, campo: 'aberto' | 'abre' | 'fecha', valor: boolean | string) =>
    definirJanelas((atuais) => atuais.map((j, i) => (i === dia ? { ...j, [campo]: valor } : j)));

  return (
    <DialogoDeAlteracao
      titulo="A organização"
      stepUp={stepUp}
      aoFechar={aoFechar}
      aoSalvar={aoSalvar}
      montarCorpo={(justificativa) => ({
        justificativa,
        timezone: fuso,
        horario_de_atendimento: janelas.flatMap((j, dia) =>
          j.aberto ? [{ diaDaSemana: dia, abre: j.abre, fecha: j.fecha }] : [],
        ),
      })}
    >
      <CampoSelecao
        rotulo="Fuso horário"
        value={fuso}
        ajuda="É ele que datou cada mensagem do histórico. Mudar não redata o que já passou."
        onChange={(e) => definirFuso(e.currentTarget.value)}
      >
        {(FUSOS as readonly string[]).includes(fuso) ? null : <option value={fuso}>{fuso}</option>}
        {FUSOS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </CampoSelecao>

      <fieldset className="alterar-config__expediente">
        <legend>Expediente</legend>
        <p className="alterar-config__ajuda">
          Dia desmarcado é dia fechado — é o que decide se a conversa entrou fora de hora.
        </p>

        {DIAS.map((nome, dia) => {
          const j = janelas[dia]!;
          return (
            <div className="alterar-config__dia" key={nome}>
              <label className="alterar-config__aberto">
                <input
                  type="checkbox"
                  checked={j.aberto}
                  onChange={(e) => trocar(dia, 'aberto', e.currentTarget.checked)}
                />
                <span>{nome}</span>
              </label>

              {/*
                As horas ficam DESABILITADAS no dia fechado, e não escondidas: a
                linha some e a lista pula de altura a cada caixa marcada, o que
                faz alguém marcar o dia errado no meio da mudança.
              */}
              <Campo
                rotuloOculto={`Abre ${nome.toLowerCase()}`}
                type="time"
                value={j.abre}
                disabled={!j.aberto}
                onChange={(e) => trocar(dia, 'abre', e.currentTarget.value)}
              />
              <span aria-hidden="true">até</span>
              <Campo
                rotuloOculto={`Fecha ${nome.toLowerCase()}`}
                type="time"
                value={j.fecha}
                disabled={!j.aberto}
                onChange={(e) => trocar(dia, 'fecha', e.currentTarget.value)}
              />
            </div>
          );
        })}
      </fieldset>
    </DialogoDeAlteracao>
  );
}

/* ── Bloco 2: reconhecer a mesma pessoa ─────────────────────────────────── */

export function AlterarAIdentidade({
  config,
  stepUp,
  aoFechar,
  aoSalvar,
}: {
  config: ConfiguracaoDoTenant;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoSalvar: () => void;
}): ReactNode {
  const [limiar, definirLimiar] = useState(Math.round(config.limiar_match_probabilistico * 100));
  const [documento, definirDocumento] = useState(config.usar_documento_na_identidade);

  return (
    <DialogoDeAlteracao
      titulo="Reconhecer a mesma pessoa"
      stepUp={stepUp}
      aoFechar={aoFechar}
      aoSalvar={aoSalvar}
      montarCorpo={(justificativa) => ({
        justificativa,
        limiar_match_probabilistico: limiar / 100,
        usar_documento_na_identidade: documento,
      })}
    >
      <Campo
        rotulo="Limiar para unir cadastros"
        type="number"
        min={0}
        max={100}
        step={1}
        value={String(limiar)}
        ajuda="Em porcentagem. Acima disto o sistema une sozinho; abaixo, manda para a revisão de identidade. Mudar NÃO retroage: a escada só decide no momento em que o contato chega."
        onChange={(e) => definirLimiar(Number(e.currentTarget.value))}
      />

      <label className="alterar-config__caixa">
        <input
          type="checkbox"
          checked={documento}
          onChange={(e) => definirDocumento(e.currentTarget.checked)}
        />
        <span>
          Usar CPF e CNPJ como sinal
          <small>
            Documento igual é a evidência mais forte que existe para dizer que são a mesma pessoa.
          </small>
        </span>
      </label>
    </DialogoDeAlteracao>
  );
}

/* ── Bloco 3: o que o sistema faz sozinho ───────────────────────────────── */

export function AlterarOAutomatico({
  config,
  stepUp,
  aoFechar,
  aoSalvar,
}: {
  config: ConfiguracaoDoTenant;
  stepUp: UsoDeStepUp;
  aoFechar: () => void;
  aoSalvar: () => void;
}): ReactNode {
  const regra = config.criar_oportunidade_regra;
  const [canais, definirCanais] = useState<readonly string[]>(regra.canais ?? []);
  const [janelaDias, definirJanelaDias] = useState(regra.janela_dias ?? 30);
  const [criarTarefa, definirCriarTarefa] = useState(
    config.encerramento_regra.criar_tarefa_de_proxima_acao === true,
  );

  const alternar = (valor: string) =>
    definirCanais((atuais) =>
      atuais.includes(valor) ? atuais.filter((c) => c !== valor) : [...atuais, valor],
    );

  return (
    <DialogoDeAlteracao
      titulo="O que o sistema faz sozinho"
      stepUp={stepUp}
      aoFechar={aoFechar}
      aoSalvar={aoSalvar}
      montarCorpo={(justificativa) => ({
        justificativa,
        /*
         * A regra ATUAL espalhada primeiro. Ver o cabeçalho: o `jsonb` é
         * substituído inteiro, e mandar só o que mudou apagaria
         * `etapas_abertas`, `risco` e o que mais estiver lá — três telas
         * passariam a se comportar diferente por causa de uma troca de canal.
         */
        criar_oportunidade_regra: {
          ...regra,
          canais: [...canais],
          /*
           * `exige_interesse_em_veiculo` NÃO aparece aqui, e é de propósito.
           *
           * A opção saiu do ar (#489), mas o valor gravado atravessa no
           * `...regra` acima em vez de ser apagado: salvar uma configuração
           * não é hora de destruir dado que outra decisão vai reavaliar. O
           * worker o ignora, então ele não muda comportamento nenhum.
           */
          janela_dias: janelaDias,
        },
        encerramento_regra: {
          ...config.encerramento_regra,
          criar_tarefa_de_proxima_acao: criarTarefa,
        },
      })}
    >
      <fieldset className="alterar-config__canais">
        <legend>Abrir oportunidade a partir de</legend>
        <p className="alterar-config__ajuda">
          Nenhum canal marcado desliga a regra: nenhuma oportunidade será aberta sozinha.
        </p>
        {CANAIS.map((c) => (
          <label className="alterar-config__caixa" key={c.valor}>
            <input
              type="checkbox"
              checked={canais.includes(c.valor)}
              onChange={() => alternar(c.valor)}
            />
            <span>{c.nome}</span>
          </label>
        ))}
      </fieldset>

      <Campo
        rotulo="Não abrir outra oportunidade se já houver uma aberta nos últimos"
        type="number"
        min={1}
        max={365}
        step={1}
        value={String(janelaDias)}
        ajuda="Em dias. É o que impede o mesmo cliente de virar cinco oportunidades numa semana de conversa."
        onChange={(e) => definirJanelaDias(Number(e.currentTarget.value))}
      />

      <label className="alterar-config__caixa">
        <input
          type="checkbox"
          checked={criarTarefa}
          onChange={(e) => definirCriarTarefa(e.currentTarget.checked)}
        />
        <span>
          Criar tarefa de próxima ação ao encerrar conversa
          <small>Conversa encerrada sem próximo passo é a que ninguém retoma.</small>
        </span>
      </label>
    </DialogoDeAlteracao>
  );
}
